import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import {environment} from '../../../../environments/environment';
import {Project, ProjectsQuery} from '../../../_core/state/project';
import {SessionQuery} from '../../../_core/state/session';

import {Business, BusinessQuery} from '../../../_core/state/business';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {debounceTime, takeUntil} from 'rxjs/operators';
import { first, firstValueFrom, of, Subject, take, catchError } from 'rxjs';
import {
	AssetTemplateSelectCopySettings,
	AssetTemplateSelectCustomFilterSettings,
	AssetTemplateSelectElement,
	AssetTemplateSelectFilter, AssetTemplateSelectionChangeEvent,
	AssetTemplateSelectSelectType,
	AssetTemplateSelectUiSettings,
	AssetTemplateSelectViewMode
} from './asset-template-selector.model';
import { Category } from '../../../_core/state/global/global.model';
import { ActionEvent, ActionType, Collection, QueryParams } from "../../../_core/state/collection/collection.model";
import { CollectionService } from "../../../_core/state/collection/collection.service";
import { CollectionQuery } from "../../../_core/state/collection/collection.query";
import { CommonModule } from "@angular/common";
import { SharedPipeModule } from "../../pipes/shared-pipe.module";
import { materialModules } from "../../../angular-material.module";
import { CollectionModule } from "../../../_core/state/collection/collection.module";
import { CloudinaryImageTransformPipe } from '../../pipes/cloudinary-image-transform.pipe';
import { getTemplatePlaceholderClass } from '../../utils/template-placeholder.util';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { adBuilderPagination } from '../../../_core/state/ad/ad-preview/ad-preview-pagination/ad-preview-pagination.component';
import { MatDialog } from "@angular/material/dialog";
import {
	AddTemplateDialogComponent,
	AddTemplateDialogDataModel
} from "../add-template-dialog/add-template-dialog.component";
import { CreativeUnitPackageService } from "../../../_core/state/ad/ad-unit-package.service";
import { ConfirmDialogComponent } from "../confirm-dialog/confirm-dialog.component";
import {
	PublicCreativeUnitPackageSummary
} from "../../../../../../api/src/creative-unit-package/creative-unit-package.entity";
import { DataImportDialogDataModel, ImportEvent } from "../data-import-dialog/data-import-dialog-data.model";
import { DataImportDialogComponent } from "../data-import-dialog/data-import-dialog.component";
import { GlobalService } from "../../../_core/state/global";
import { AdEditorHelperService } from "../../../pages/sandbox/ad-editor/ad-editor-helper.service";
import { DateUtils } from '../../../_core/utils/date.utils';

@Component({
	selector: 'app-template-selector',
	standalone: true,
		imports: [
			CommonModule,
			...materialModules,
			ReactiveFormsModule,
			SharedPipeModule,
			CollectionModule,
		],
	templateUrl: './asset-template-selector.component.html',
	styleUrls: ['./asset-template-selector.component.scss'],
	providers: [{ provide: MatPaginatorIntl, useClass: adBuilderPagination }]
})
export class AssetTemplateSelectorComponent implements OnInit, OnDestroy {
	protected readonly AssetTemplateSelectViewMode = AssetTemplateSelectViewMode;
	protected readonly ActionType = ActionType;

	readonly DEFAULT_UI_SETTINGS: AssetTemplateSelectUiSettings = {
		showFilter: true,
		showSearch: true,
		showFileTypeFilter: false,
		showFileType: true,
		showRecent: true,
		showCategory: true,
		showMoreFilters: true,
		showSelectViewMode: true,
		showAddButton: true,
		showCancelButton: true,
		showFeatured: true,
		groupByCategory: true,
		showPreview: true,
		showSubmitButton: true,
		showPackageMenuActions: false,
		showImportButton: false
	};
	readonly DEFAULT_COPY_SETTINGS: AssetTemplateSelectCopySettings = {
		elementTitle: 'format',
		elementTitlePlural: 'formats',
		featuredTitle: 'Featured',
		recentTitle: 'Recently Added',
		searchPlaceholder: 'Search',
		noResultsText: 'No results'
	};

	@ViewChild('resultsContainer') resultsContainer: ElementRef;

	@Input() elements: AssetTemplateSelectElement[];
	@Input() selectType: AssetTemplateSelectSelectType = 'single';
	@Input() viewMode: AssetTemplateSelectViewMode = 'card';
	@Input() viewModeOptions: AssetTemplateSelectViewMode[];

	_tableCollection: Collection;
	@Input() set tableCollection(collection: Collection) {
		this._tableCollection = collection;

		// Set as active if we don't have an active collection yet or if we're in table mode
		if (!this.activeCollection || this.viewMode === 'table') {
			this.activeCollection = collection;
		}
	}
	get tableCollection(): Collection {
		return this._tableCollection;
	}

	_gridCollection: Collection;
	@Input() set gridCollection(collection: Collection) {
		this._gridCollection = collection;

		// Set as active if we're in card mode
		if (this.viewMode === 'card') {
			this.activeCollection = collection;
		} else if (!this.activeCollection) {
			// Fallback to grid if no active collection is set
			this.activeCollection = collection;
		}
	}
	get gridCollection(): Collection {
		return this._gridCollection;
	}

	private activeCollection: Collection;

	@Input() set uiSettings(settings: Partial<AssetTemplateSelectUiSettings>) {
		this.ui = {...this.DEFAULT_UI_SETTINGS, ...settings};
	}

	@Input() set copySettings(settings: Partial<AssetTemplateSelectCopySettings>) {
		this.copy = {...this.DEFAULT_COPY_SETTINGS, ...settings};
	}

	@Input() set selectedElements(elements: AssetTemplateSelectElement[]) {
		this.selectedElementIds = elements.map((element) => element.id);
	}

	@Input() set filters(filters: AssetTemplateSelectFilter) {
		if (!this.filterForm) {
			this._createFilterForm();
		}

		this.filterForm.patchValue(filters);
	}

	@Input() categories: Category[] = [];


	@Output() add: EventEmitter<AddTemplateDialogDataModel | null> = new EventEmitter<AddTemplateDialogDataModel | null>();
	@Output() cancel: EventEmitter<void> = new EventEmitter<void>();
	@Output() selectionChange: EventEmitter<AssetTemplateSelectionChangeEvent> = new EventEmitter<AssetTemplateSelectionChangeEvent>();
	@Output() pinToggle: EventEmitter<AssetTemplateSelectElement> = new EventEmitter<AssetTemplateSelectElement>();

	public ui: AssetTemplateSelectUiSettings = this.DEFAULT_UI_SETTINGS;
	public copy: AssetTemplateSelectCopySettings = this.DEFAULT_COPY_SETTINGS;
	public filterForm: UntypedFormGroup;
	public selectedElementIds: string[] = [];
	public currentFilters: AssetTemplateSelectFilter;
	private _unsubscribe: Subject<void> = new Subject<void>();

	public isAdmin: boolean = false;
	private siteId: string = environment.organizationId;
	public project: Project | undefined;
	public business: Business | undefined;

	public formatDate = DateUtils.formatDate;

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly sessionQuery: SessionQuery,
		private readonly projectQuery: ProjectsQuery,
		private readonly businessQuery: BusinessQuery,
		private readonly collectionQuery: CollectionQuery,
		private readonly collectionService: CollectionService,
		private readonly creativeUnitPackageService: CreativeUnitPackageService,
		private readonly dialog: MatDialog,
		private readonly globalService: GlobalService,
		private readonly adEditorHelperService: AdEditorHelperService
	) {
	}

		ngOnInit() {
			// Restore persisted viewMode if available
			const savedViewMode = localStorage.getItem('assetTemplateSelectorViewMode');
			if (savedViewMode && this.viewModeOptions?.includes(savedViewMode as AssetTemplateSelectViewMode)) {
				this.setViewMode(savedViewMode as AssetTemplateSelectViewMode);
			}
			this.isAdmin = this.sessionQuery.isAtLeastAdmin();
			this.project = this.projectQuery.getActive();
			this.business = this.project?.business ? this.project.business : this.businessQuery.getActive();

			this._createFilterForm();
		}

	ngOnDestroy() {
		this._unsubscribe.next();
		this._unsubscribe.complete();
	}

	toggleSelection(element: AssetTemplateSelectElement, openSelection = false): void {
		if (this.selectType === 'single') {
			if (this.selectedElementIds.includes(element.id)) {
				this.selectedElementIds = [];
			}else{
				this.selectedElementIds = [element.originalVersionId || element.id];
			}
		} else {
			const index = this.selectedElementIds.indexOf(element.id);
			if (index === -1) {
				this.selectedElementIds.push(element.originalVersionId || element.id);
			} else {
				this.selectedElementIds.splice(index, 1);
			}
		}

		this.selectionChange.emit({
			selection: this.selectedElementIds,
			openSelection
		});
	}

	onSelectSubCategory(category: Category, value: Category): void {
		let control = this.filterForm.get('categories')?.get(category.slug);
		if (control) {
			// Reset if already selected.
			if (control.value?.id === value.id) {
				control.setValue(undefined);
				return;
			}

			control.setValue(value);
		}
	}

	onSelectRecentFilter(showRecent: boolean): void {
		this.filterForm.patchValue({
			showRecent
		});
	}

	onCustomFilterChange(filter: AssetTemplateSelectCustomFilterSettings, value: string | string[]): void {
		this.filterForm.patchValue({
			customFilters: {
				...this.filterForm.value.customFilters,
				[filter.key]: (value === '' || filter.selectionType === 'multiple' && !value?.length) ? undefined : filter.selectionType === 'multiple'? value : [value]
			}
		});
	}

	generateBackground(url: string, selected: boolean): string {
		// Return just the URL without any gradient overlay
		return `url(${url})`;
	}

	getCloudinaryThumbnail(url: string): string {
		return new CloudinaryImageTransformPipe().transform(url, { width: 350, height: 150, mode: 'fill', gravity: 'auto' });
	}

	getPlaceholderClass(elementName: string, index?: number): string {
		return getTemplatePlaceholderClass(elementName, index);
	}

	elementFilterFn(element: AssetTemplateSelectElement, filters: AssetTemplateSelectFilter, isFeatured?: boolean): boolean {
		if (isFeatured != undefined && element.isFeatured !== isFeatured) {
			return false;
		}

		if (!filters) {
			return true;
		}

		if (filters.showRecent && !element.isRecent) {
			return false;
		}

		if (filters.search) {
			const name = element.name?.toLowerCase() || '';
			const tags = (element.tags || []).join(' ').toLowerCase();
			const search = filters.search.toLowerCase();

			if (!name.includes(search) && !tags.includes(search)) {
				return false;
			}
		}

		if (filters.fileTypes?.length && !filters.fileTypes.includes(element.fileType)) {
			return false;
		}

		for (let category of Object.keys(filters?.categories || {})) {
			if (filters.categories[category] && element.categories?.[category]?.id !== filters.categories[category].id) {
				return false;
			}
		}

		if (filters.customFilters) {
			for (const key in filters.customFilters) {
				if (filters.customFilters.hasOwnProperty(key)) {
					const filter = filters.customFilters[key];
					if (filter !== undefined && !filter.includes(element[key])) {
						return false;
					}
				}
			}
		}

		return true;
	}

	getCustomFilterValues(customFilter: AssetTemplateSelectCustomFilterSettings, elements: AssetTemplateSelectElement[]): {value: string, label: string }[] {
		if (customFilter.options === 'auto') {
			return [...new Set(elements.map((element) => element[customFilter.key]))].map((value) => ({value, label: value?.toString() || ''}));
		} else {
			return customFilter.options;
		}
	}

	getFileTypes(elements: AssetTemplateSelectElement[]): string[] {
		return [...new Set(elements.map((element) => element.fileType))];
	}

	isCustomFilterActive(currentFilters: AssetTemplateSelectFilter): boolean {
		return Object.values(currentFilters.customFilters || {}).findIndex(v => !!v) > -1;
	}

	private _createFilterForm() {
		this.filterForm = this.fb.group({
			search: [''],
			fileTypes: [''],
			showRecent: [false],
			customFilters: ['']
		});

		if (this.categories.length) {
			let formGroup = this.fb.group({});
			this.categories.forEach(category => {
				formGroup.addControl(category.slug, this.fb.control(undefined));
			});

			this.filterForm.addControl('categories', formGroup);
		}

		this.filterForm.valueChanges.pipe(
			debounceTime(200),
			takeUntil(this._unsubscribe)
		).subscribe((value) => {
			this.currentFilters = value;
			
			// Update the active collection based on current view mode
			const targetCollection = this.viewMode === 'table' ? this.tableCollection : this.gridCollection;
			if (targetCollection?.id) {
				this.collectionService.updateQueryParams(targetCollection.id, {
					...this.currentFilters,
					page: 1,
				});
			}

			// Also update the other collection to keep them in sync
			const otherCollection = this.viewMode === 'table' ? this.gridCollection : this.tableCollection;
			if (otherCollection?.id && otherCollection.id !== targetCollection?.id) {
				this.collectionService.updateQueryParams(otherCollection.id, {
					...this.currentFilters,
					page: 1,
				});
			}
		});
	}

	async handleTableEvents({entity, action}: ActionEvent) {
		console.log('🎭 AssetTemplateSelector.handleTableEvents:', action.type, 'Entity:', { name: entity.name, isPinned: entity.isPinned });
		switch (action.type) {
			case ActionType.SELECT:
				this.selectFromTable(entity);
				break;
			case ActionType.REMOVE:
				await this.removeFromTable(entity);
				break;
			case ActionType.EXPORT:
				await this.exportPackage(entity);
				break;
			default:
				console.log("Could not found the action");
			case ActionType.PIN:
				this.handlePinToggle(entity);
				break;
		}
	}

	handlePinToggle(element: AssetTemplateSelectElement, event?: Event) {
		if (event) {
			event.stopPropagation();
		}
		this.pinToggle.emit(element);
	}

	selectFromTable(entity: any, manual: boolean = false) {
		const initiateSelectOnClick = !this.ui.showSubmitButton;
		if (initiateSelectOnClick) {
			this.toggleSelection(entity, true);
			return;
		}

		if (manual) {
			this.collectionService.toggleSelectForEntities(this.activeCollection.id, [entity]);
		}

		const collection = this.collectionQuery.getEntity(this.activeCollection.id);

		if (this.selectType === 'single') {
			if (collection.selectedEntities.length > 1) {
				this.collectionService.clearSelectedEntities(this.activeCollection.id);
				this.collectionService.toggleSelectForEntities(this.activeCollection.id, [entity]);
			}
		}

		this.toggleSelection(entity);
	}

		setViewMode(newMode: any) {
			this.viewMode = newMode;
			localStorage.setItem('assetTemplateSelectorViewMode', newMode);
			this.activeCollection = newMode === 'table' ? this.tableCollection : this.gridCollection;

			// Ensure the active collection has the current search filters applied
			if (this.activeCollection && this.currentFilters) {
				let queryParams: QueryParams = {
					...this.activeCollection.queryParams,
					...this.currentFilters,
					page: 1
				};
				this.activeCollection.queryParams = queryParams;

				// Update the collection service with the current filters
				this.collectionService.updateQueryParams(this.activeCollection.id, queryParams);
			}

			this.resultsContainer?.nativeElement?.scrollTo({
				top: 0,
				behavior: 'instant'
			});

			setTimeout(() => {
				// Import selections from one view to another
				if (newMode === 'table' && this.activeCollection) {
					this.collectionService.clearSelectedEntities(this.activeCollection.id);
					this.collectionService.toggleSelectForEntities(this.activeCollection.id, [this.selectedElementIds.map(id => { return { id: id }})]);
				}
			}, 1);
		}

	openTemplateCreationDialog() {
		this.dialog
			.open(AddTemplateDialogComponent, {
				width: '640px',
				disableClose: true,
				data: {
					elementTitle: this.copy.elementTitle
				}
			})
			.afterClosed()
			.pipe(first())
			.subscribe(result => {
				this.add.emit(result)
			});
	}

	async removeFromTable(entity: PublicCreativeUnitPackageSummary) {
		const name = entity.name;
		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: $localize `:Delete Confirm Dialog@@adPackageSettingsDeleteConfirmationDialogTitle:Remove Template`,
				message:$localize`:Delete Confirm Dialog@@adPackageSettingsDeleteTemplateConfirmationDialogMessage:Are you sure you want to remove the Template: <b>${name}</b>?`,
				remove: true
			}
		});

		const confirmed = await firstValueFrom(dialogRef.afterClosed())
		if (confirmed) {
			this.creativeUnitPackageService.remove(entity).pipe(first()).subscribe(() => {
				this.collectionService.updateQueryParams(this.activeCollection.id, {
					...this.collectionQuery.getEntity(this.activeCollection.id)?.queryParams,
				})
			});
		}
	}

	public async importTemplate() {
		const dialogSettings: DataImportDialogDataModel = {
			event: ImportEvent.Other,
			title: $localize`:Import Package JSON Title@@importPackageJsonTitle:Import Creative Package`,
			showCancelButton: true,
			showDialogCloseButton: true,
			uploadAccept: '.json, .ccetemplate'
		};

		this.dialog
			.open(DataImportDialogComponent, {
				width: '640px',
				panelClass: ['background-color-alternative', 'max-width-lg'],
				data: dialogSettings
			})
			.afterClosed()
			.pipe(first())
			.subscribe((response: any) => {
				if (!response || response.length === 0) {
					return;
				}
				const file = response[0]?.file;
				if (!file) {
					this.globalService.triggerCustomMessage('Incorrect file selected', 'error');
				}

				const reader = new FileReader();
				reader.onload = (event: ProgressEvent<FileReader>) => {
					if (!event.target?.result) {
						console.error('Error reading file');
						return;
					}

					try {
						const jsonData = JSON.parse(event.target.result as string);

						/**
						 * Delete or update following properties as they should not transfer to the active package
						 * and patch the modified date to current time
						 */
						const data = {
							...jsonData,
							deleted: false,
							created: new Date().toISOString(),
							modified: new Date().toISOString(),
							priceProductId: undefined,
							originalVersionId: undefined,
							versionComment: undefined,
							status: undefined,
							publishedAt: undefined
						};

						this.creativeUnitPackageService
							.createPackageFromJson(data)
							.pipe(
								take(1),
								catchError(error => {
									this.globalService.triggerErrorMessage(error);
									return of(null);
								})
							)
							.subscribe((data: any) => {
								if (data) {
									this.globalService.triggerSuccessMessage('Creative package imported successfully');

									this.selectionChange.emit({
										selection: [data.id],
										openSelection: true
									});
								}
							});

					} catch (error) {
						console.error('Error parsing JSON:', error);
						this.globalService.triggerErrorMessage(error);
					}
				}

				reader.readAsText(file);
			});
	}

	public async exportPackage(entity: any) {
		try {
			const cPackage = await this.creativeUnitPackageService.findOne(entity.id).toPromise();
			await this.adEditorHelperService.exportCreativePackage(cPackage);
		} catch (error) {
			this.globalService.triggerErrorMessage(error);
			console.error('Error exporting package:', error);
		}
	}

	canExportTemplate(): boolean {
		// Check if user is super admin
		if (this.sessionQuery.isAtLeastSuperAdmin()) {
			return true;
		}
		
		// Check if user has 'ad-editor' permission
		return this.sessionQuery.hasAdEditorPermission();
	}
}
